import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash } from '@saleshandy/icons';
import Button from '../../../../../../shared/design-system/components/atoms/button';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import Select from '../../../../../../shared/design-system/components/select';
import { capitalize } from '../../../../../../shared/utils';
import { Placement } from '../../../../../../shared/design-system/components/overlay';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { CallOutcome } from '../../types';
import {
  getCallOutcomeIcon,
  getDeleteCallOutcomeDropdownOptions,
} from '../call-outcome-content/helpers/call-outcome-column-data';

type DeleteCallOutcomeDropdown = {
  key: number;
  name: string;
  sentiment: string;
  bgColor: string;
  textColor: string;
  isDefault: boolean;
};

type DeleteCallOutcomeModalProps = {
  show: boolean;
  onSubmit: () => void;
  onClose: () => void;
  isRequestPending: boolean;
  onOutcomeIdUpdate: (outcomeId: number) => void;
  options: CallOutcome[];
  sendAllOutcomesRequest: () => void;
  getAllOutcomesRequestStatus: RequestStatus;
  actionCustomOutcome: CallOutcome;
};

const DeleteCallOutcomeModal: React.FC<DeleteCallOutcomeModalProps> = ({
  show,
  onSubmit,
  onClose,
  isRequestPending,
  onOutcomeIdUpdate,
  options,
  sendAllOutcomesRequest,
  getAllOutcomesRequestStatus,
  actionCustomOutcome,
}) => {
  const { t } = useTranslation();
  const [selectedOutcomeId, setSelectedOutcomeId] = useState<number>(1);

  useEffect(() => {
    if (show && getAllOutcomesRequestStatus !== RequestStatus.Pending) {
      sendAllOutcomesRequest();
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal
      show={show}
      className="delete-modal delete-call-outcome-modal"
      titleContent={
        <div className="d-flex align-items-center delete-modal-title">
          <Trash width={23} height={23} className="red-txt-12" />
          <div className="delete-title">Delete Outcome</div>
        </div>
      }
      showCloseIcon={false}
      onClose={onClose}
      cancelButtonText={t('labels.cancel')}
      submitButtonText={t('labels.delete')}
      isSubmitLoading={isRequestPending}
      isSubmitDisabled={isRequestPending}
      onSubmit={onSubmit}
      cancelButtonVarient={Button.Variant.Outlined}
      onHide={onClose}
      extraModalProps={{
        enforceFocus: false,
      }}
    >
      <span className="regular-2 popover-arrow-color-txt">
        Deleting this outcome will leave all associated calls uncategorized.
      </span>
      <span className="regular-2 popover-arrow-color-txt">
        Please select a new outcome for them.
      </span>
      <Select<DeleteCallOutcomeDropdown>
        showSearch
        className="delete-call-outcome-transfer"
        filterOption={(value, option) =>
          option.name.toLowerCase().includes(value.toLowerCase())
        }
        selectedOptionRenderer={([option]) => <span>{option?.name}</span>}
        optionRenderer={(option) => (
          <div className="sentiment-content">
            <div className="sentiment-icon">
              {getCallOutcomeIcon(option.name, option.isDefault, option.sentiment, {
                width: 16,
                height: 16,
              })}
            </div>
            <div>{capitalize(option.name)}</div>
          </div>
        )}
        options={getDeleteCallOutcomeDropdownOptions(
          options,
          actionCustomOutcome?.id,
        )}
        placeholder="Select Outcome"
        searchPlaceholder="Search"
        placement={Placement.BottomEnd}
        selectedOptionKey={selectedOutcomeId}
        onChange={([selectedOption]) => {
          setSelectedOutcomeId(selectedOption?.key);
          onOutcomeIdUpdate(selectedOption?.key);
        }}
      />
    </Modal>
  );
};

export default DeleteCallOutcomeModal;
